import {
  createAuraRenderer,
  resolveBubbleColor,
} from "@moss-js/founding-agent/core";
import type { AuraRenderer } from "@moss-js/founding-agent/core";
import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore, BubbleColorInput } from "@moss-js/founding-agent/core";

export interface AuraOpts {
  color: BubbleColorInput;
  themeMode: "light" | "dark";
}

export interface AuraHandle {
  canvas: HTMLCanvasElement;
  destroy(): void;
}

// Paint a static aura disc (2D radial gradient) for the non-shader fallbacks
// so the canvas is never blank. No-op where 2D context is unavailable (jsdom).
function paintStaticAura(canvas: HTMLCanvasElement, color: `#${string}`): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  canvas.width = 96;
  canvas.height = 96;
  const r = canvas.width / 2;
  const gradient = ctx.createRadialGradient(r, r, 0, r, r, r);
  gradient.addColorStop(0, color);
  gradient.addColorStop(0.65, color);
  gradient.addColorStop(1, `${color}00`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}

export function auraVolumeForState(
  agentDisplayState: VoiceAgentStore["agentDisplayState"],
  agentAudioLevel: number,
): number {
  // The renderer only drives scale from volume while speaking; feeding a value
  // in any other state is inert and would fire setVolume needlessly (~25Hz).
  return agentDisplayState === "speaking" ? agentAudioLevel : 0;
}

export function createAura(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
  opts: AuraOpts,
): AuraHandle {
  const canvas = document.createElement("canvas");
  canvas.style.cssText = "width:48px;height:48px;border-radius:50%;display:block;";

  root.appendChild(canvas);

  const resolvedColor = resolveBubbleColor(opts.color);
  let renderer: AuraRenderer;

  // Probe WebGL support with a throwaway canvas before constructing the renderer
  const probeCanvas = document.createElement("canvas");
  const hasWebGL =
    typeof WebGLRenderingContext !== "undefined" &&
    probeCanvas.getContext("webgl") !== null;

  const reduceMotion =
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduceMotion) {
    // Static orb: no shader loop, no CSS pulse, but still a visible disc.
    renderer = { setState() {}, setVolume() {}, destroy() {} };
    paintStaticAura(canvas, resolvedColor);
  } else if (!hasWebGL) {
    renderer = { setState() {}, setVolume() {}, destroy() {} };
    paintStaticAura(canvas, resolvedColor);
    canvas.style.animation = "moss-aura-pulse 1.6s ease-in-out infinite";
  } else {
    try {
      renderer = createAuraRenderer(canvas, { color: resolvedColor, themeMode: opts.themeMode });
    } catch {
      renderer = { setState() {}, setVolume() {}, destroy() {} };
      paintStaticAura(canvas, resolvedColor);
      canvas.style.animation = "moss-aura-pulse 1.6s ease-in-out infinite";
    }
  }

  // Apply current state before subscribing: createAura can run mid-conversation
  // (setTheme rebuilds it), and the subscription only fires on later writes.
  const initial = store.getState();
  renderer.setState(initial.agentDisplayState);
  renderer.setVolume(auraVolumeForState(initial.agentDisplayState, initial.agentAudioLevel));

  const unsub = store.subscribe((state, prev) => {
    if (state.agentDisplayState !== prev.agentDisplayState) {
      renderer.setState(state.agentDisplayState);
    }
    const vol = auraVolumeForState(state.agentDisplayState, state.agentAudioLevel);
    const prevVol = auraVolumeForState(prev.agentDisplayState, prev.agentAudioLevel);
    if (vol !== prevVol) {
      renderer.setVolume(vol);
    }
  });

  return {
    canvas,
    destroy() {
      unsub();
      renderer.destroy();
      canvas.remove();
    },
  };
}
